import { useCallback, useMemo, useRef } from 'react';
import { ConcurrentExperience } from '@atlassian/ufo-experimental/concurrent-experience';
import {
	ExperiencePerformanceTypes,
	ExperienceTypes,
} from '@atlassian/ufo-experimental/experience-types';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { useExperienceAbort } from '@jira/platform__jira-experience-tracker/src/ui/experience-abort/index.tsx';
import { useExperienceFail } from '@jira/platform__jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { useExperienceStart } from '@jira/platform__jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useExperienceSuccess } from '@jira/platform__jira-experience-tracker/src/ui/experience-success/index.tsx';

const PACKAGE_NAME = 'jiraAiPlanningAiPlannerIframeModal';
const TEAM_NAME = 'dev-ai-planner';
export const AI_PLANNER_IFRAME_ANALYTICS_SOURCE = 'ai-planner';
export const AI_PLANNER_IFRAME_EXPERIENCE_TIMEOUT_MS = 15_000;

const EXPERIENCE_BY_KIND = {
	create: 'ai-planner-create-iframe-load',
	plan: 'ai-planner-plan-iframe-load',
} as const;

const iframeLoadLatency = {
	create: new ConcurrentExperience(EXPERIENCE_BY_KIND.create, {
		type: ExperienceTypes.Load,
		performanceType: ExperiencePerformanceTypes.Custom,
	}),
	plan: new ConcurrentExperience(EXPERIENCE_BY_KIND.plan, {
		type: ExperienceTypes.Load,
		performanceType: ExperiencePerformanceTypes.Custom,
	}),
} as const;

export type AiPlannerIframeKind = 'create' | 'plan';
export type AiPlannerIframeSurface = 'modal' | 'page';
export type AiPlannerIframeFailureReason = 'entry-point' | 'error' | 'render' | 'timeout';

/**
 * Which render path served this open. A load is only comparable to another load
 * on the same path — the shared iframe is often pre-warmed, the standalone paths
 * are always cold — so every settled experience carries it.
 */
export type AiPlannerIframeRenderPath =
	| 'shared'
	| 'standalone'
	| 'standalone-unsupported'
	| 'standalone-move-failed';

export type AiPlannerIframeLoadAttributes = {
	renderPath?: AiPlannerIframeRenderPath;
	/**
	 * True when the planner was already showing the requested content, so the
	 * user waited for nothing. The measure of whether pre-warming is paying off.
	 */
	fromWarmShell?: boolean;
};

export type AiPlannerIframeLoadExperience = {
	experience: 'ai-planner-create-iframe-load' | 'ai-planner-plan-iframe-load';
	experienceId: string;
	start: () => void;
	succeed: (attributes?: AiPlannerIframeLoadAttributes) => void;
	fail: (
		reason: AiPlannerIframeFailureReason,
		error?: Error,
		options?: { skipErrorAnalytics?: boolean; attributes?: AiPlannerIframeLoadAttributes },
	) => void;
	abort: (reason: string) => void;
};

const createIframeLoadError = (reason: AiPlannerIframeFailureReason): Error =>
	new Error(`AI Planner iframe load failed: ${reason}`);

export const useAiPlannerIframeLoadExperience = ({
	kind,
	surface,
}: {
	kind: AiPlannerIframeKind;
	surface: AiPlannerIframeSurface;
}): AiPlannerIframeLoadExperience => {
	const experience = EXPERIENCE_BY_KIND[kind];
	const experienceId = `${surface}-${kind}`;
	const attributes = useMemo(() => ({ iframeKind: kind, surface }), [kind, surface]);
	const startExperience = useExperienceStart({
		experience,
		experienceId,
		analyticsSource: AI_PLANNER_IFRAME_ANALYTICS_SOURCE,
		timeout: AI_PLANNER_IFRAME_EXPERIENCE_TIMEOUT_MS,
	});
	const markSucceeded = useExperienceSuccess({ experience, attributes });
	const markFailed = useExperienceFail({ experience, attributes });
	const markAborted = useExperienceAbort({ experience, attributes });
	const attemptSettledRef = useRef(false);
	const latencyAttemptRef = useRef(0);
	const latencyInstanceIdRef = useRef<string | null>(null);
	const endLatency = useCallback(
		(outcome: 'success' | 'abort') => {
			const instanceId = latencyInstanceIdRef.current;
			if (instanceId == null) {
				return;
			}
			latencyInstanceIdRef.current = null;
			const instance = iframeLoadLatency[kind].getInstance(instanceId);
			(outcome === 'success' ? instance.success() : instance.abort()).catch(() => undefined);
		},
		[kind],
	);

	const start = useCallback(() => {
		if (latencyInstanceIdRef.current != null) {
			endLatency('abort');
		}
		attemptSettledRef.current = false;
		const instanceId = `${experienceId}-${latencyAttemptRef.current + 1}`;
		latencyAttemptRef.current += 1;
		latencyInstanceIdRef.current = instanceId;
		startExperience();
		iframeLoadLatency[kind]
			.getInstance(instanceId)
			.start()
			.catch(() => undefined);
	}, [endLatency, experienceId, kind, startExperience]);

	const succeed = useCallback(
		(loadAttributes: AiPlannerIframeLoadAttributes = {}) => {
			if (attemptSettledRef.current) {
				return;
			}
			attemptSettledRef.current = true;
			markSucceeded(loadAttributes);
			endLatency('success');
		},
		[endLatency, markSucceeded],
	);

	const fail = useCallback(
		(
			reason: AiPlannerIframeFailureReason,
			error = createIframeLoadError(reason),
			{
				skipErrorAnalytics = false,
				attributes: loadAttributes = {},
			}: { skipErrorAnalytics?: boolean; attributes?: AiPlannerIframeLoadAttributes } = {},
		) => {
			if (attemptSettledRef.current) {
				return;
			}
			attemptSettledRef.current = true;
			markFailed(`ai-planner-iframe.${surface}`, error, { reason, ...loadAttributes });
			endLatency('abort');
			if (skipErrorAnalytics) {
				return;
			}
			fireErrorAnalytics({
				meta: {
					id: 'iframeLoad',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				attributes: {
					...attributes,
					...loadAttributes,
					reason,
				},
				error,
				sendToPrivacyUnsafeSplunk: false,
			});
		},
		[attributes, endLatency, markFailed, surface],
	);

	const abort = useCallback(
		(reason: string) => {
			if (attemptSettledRef.current) {
				return;
			}
			attemptSettledRef.current = true;
			markAborted(reason);
			endLatency('abort');
		},
		[endLatency, markAborted],
	);

	return useMemo(
		() => ({ experience, experienceId, start, succeed, fail, abort }),
		[abort, experience, experienceId, fail, start, succeed],
	);
};
